import { useState, useEffect, useRef, useCallback } from 'react';
import { appendTutorCaptionDelta, sanitizeTutorCaption } from './captionSanitizer';
import type { MockTestReport } from './mockTestReport';

export type { MockTestReport } from './mockTestReport';

export interface Transcript {
  id: string;
  role: 'user' | 'aura';
  text: string;
  isFinal?: boolean;
}

/** Teil 3 picture card pushed from the backend; image is optional (labelled fallback). */
export interface PictureCard {
  cardId: string;
  germanLabel: string;
  englishLabel: string;
  imageFilename: string;
}

export const useAudioStream = (serverUrl: string = 'ws://localhost:8080', sessionContext: any = null, shouldConnect: boolean = true) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userJustFinished, setUserJustFinished] = useState(false);
  const [userNeedsCorrection, setUserNeedsCorrection] = useState(false);
  const [isGeminiReady, setIsGeminiReady] = useState(false);
  const [isMemorizing, setIsMemorizing] = useState(false);
  const [memorySaved, setMemorySaved] = useState(false);
  const [savedMemory, setSavedMemory] = useState<any>(null);
  const [isGeminiDropped, setIsGeminiDropped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [activeCard, setActiveCard] = useState<PictureCard | null>(null);
  const [mockResult, setMockResult] = useState<MockTestReport | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const captureContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const recognitionRef = useRef<any>(null);
  const isCleanedUp = useRef(false);
  const audioSentRef = useRef(false); // one-time log gate

  const playSuccessTing = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
      osc.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.1); // Slide to A6
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.5);
    } catch(e) {}
  }, []);

  const playErrorThud = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.1);
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    } catch(e) {}
  }, []);

  // Audio playback queue
  const audioQueueRef = useRef<Float32Array[]>([]);
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Queued audio playback — schedules chunks back-to-back with no overlap
  const playAudioChunk = useCallback((base64Data: string) => {
    try {
      const ctx = getAudioContext();

      // Decode base64 to PCM16
      const binaryString = window.atob(base64Data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert PCM16 (Int16) to Float32
      const audioData = new Int16Array(bytes.buffer);
      const floatData = new Float32Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        floatData[i] = audioData[i] / 32768.0;
      }

      if (floatData.length === 0) return;

      // Gemini outputs 24kHz PCM16 audio
      const sampleRate = 24000;
      const audioBuffer = ctx.createBuffer(1, floatData.length, sampleRate);
      audioBuffer.getChannelData(0).set(floatData);

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      // Schedule this chunk to play right after the previous one ends
      const now = ctx.currentTime;
      const startTime = Math.max(now, nextPlayTimeRef.current);
      source.start(startTime);

      // Track when this chunk ends so the next one starts after it
      nextPlayTimeRef.current = startTime + audioBuffer.duration;

      setIsSpeaking(true);

      source.onended = () => {
        // Only set speaking to false if no more audio is queued
        if (ctx.currentTime >= nextPlayTimeRef.current - 0.05) {
          setIsSpeaking(false);
        }
      };
    } catch (e) {
      console.error('Audio playback error:', e);
    }
  }, [getAudioContext]);

  useEffect(() => {
    if (!sessionContext || !shouldConnect) return; // Wait for context and explicit start before connecting

    isCleanedUp.current = false;
    const evalTimers: ReturnType<typeof setTimeout>[] = [];

    const ws = new WebSocket(serverUrl);

    ws.onopen = () => {
      if (isCleanedUp.current) { ws.close(); return; }
      setIsConnected(true);
      setError(null);
      console.log('Connected to AURA Realtime Server');

      // Small delay to survive React Strict Mode unmount/remount cycle
      const startTimer = setTimeout(() => {
        if (!isCleanedUp.current && ws.readyState === WebSocket.OPEN) {
          console.log('Sending start_session to server...');
          ws.send(JSON.stringify({
            type: 'start_session',
            context: sessionContext
          }));
        }
      }, 500);
      evalTimers.push(startTimer);
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'gemini_ready') {
          console.log('Gemini is ready for audio!');
          setIsGeminiReady(true);
        }
        if (data.type === 'voice_pipeline_pending') {
          console.warn('[AURA] Native voice pipeline pending:', data.message);
          setError(data.message || 'Native voice pipeline is not connected yet.');
        }
        if (data.type === 'error') {
          console.error('Server error:', data.message);
          setError(data.message || 'Server error');
          setIsProcessing(false);
        }

        if (data.type === 'evaluation') {
          setIsProcessing(false);
          if (data.status === 'success') {
            setUserJustFinished(true);
            playSuccessTing();
            const t = setTimeout(() => setUserJustFinished(false), 2000);
            evalTimers.push(t);
          } else {
            setUserNeedsCorrection(true);
            playErrorThud();
            const t = setTimeout(() => setUserNeedsCorrection(false), 2000);
            evalTimers.push(t);
          }
        }

        if (data.type === 'teil3_card' && data.card) {
          setActiveCard(data.card as PictureCard);
        }

        if (data.type === 'mock_test_result' && data.report) {
          setMockResult(data.report as MockTestReport);
        }

        if (data.type === 'memorizing_started') {
          setIsMemorizing(true);
        }

        if (data.type === 'gemini_closed') {
          // Legacy server close signal; end the session so memory is saved gracefully.
          // Auto-trigger the end_session flow so memory is saved gracefully.
          console.log('[AURA] Gemini connection closed by server — triggering graceful end.');
          setIsGeminiDropped(true);
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'end_session' }));
          }
          stopAudioCapture();
        }

        if (data.type === 'memory_saved') {
          setIsMemorizing(false);
          setMemorySaved(true);
          if (data.memory) setSavedMemory(data.memory);
        }

        if (data.type === 'transcript') {
          const transcriptText = data.role === 'aura'
            ? sanitizeTutorCaption(data.text || '')
            : data.text;

          if (data.role === 'aura' && !transcriptText && !data.isFinal) return;

          // ── User transcript ────────────────────────────────────────────────
          // Gemini native transcription emits finals only — drive orb + evaluator.
          // isUserSpeaking is controlled by local VAD in startAudioCapture.
          if (data.role === 'user' && data.isFinal) {
            setIsUserSpeaking(false);
            setIsProcessing(true); // waiting for evaluation result
          }
          if (data.role === 'aura' && data.isFinal) {
            // Free-conversation turns do not always emit an evaluation event.
            // Once AURA has replied, the next typed turn must be allowed.
            setIsProcessing(false);
          }

          setTranscripts(prev => {
            const last = prev[prev.length - 1];

            if (data.role === 'user') {
              // Each Gemini user transcript is a standalone final utterance — new bubble
              return [...prev, {
                id: Date.now().toString() + Math.random().toString(),
                role: 'user',
                text: transcriptText,
                isFinal: true
              }];
            }

            // ── AURA transcript ──────────────────────────────────────────────
            // Gemini streams outputTranscription as partial chunks (isFinal=false)
            // and sets isFinal=true on turnComplete. Merge into a single bubble.
            if (last && last.role === 'aura') {
              const newTranscripts = [...prev];
              if (!last.isFinal) {
                // Gemini outputTranscription sends incremental DELTAS — append, not replace
                newTranscripts[newTranscripts.length - 1] = {
                  ...last,
                  text: appendTutorCaptionDelta(last.text, data.text || ''),
                  isFinal: data.isFinal
                };
              } else {
                // Previous AURA bubble was final — new AURA turn starting
                return [...prev, {
                  id: Date.now().toString() + Math.random().toString(),
                  role: 'aura',
                  text: transcriptText,
                  isFinal: data.isFinal
                }];
              }
              return newTranscripts;
            }

            // No previous bubble of same role — start a new one
            return [...prev, {
              id: Date.now().toString() + Math.random().toString(),
              role: data.role,
              text: transcriptText,
              isFinal: data.isFinal
            }];
          });
        }

        if (data.type === 'audio' && data.data) {
          playAudioChunk(data.data);
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      setIsGeminiReady(false);
      setIsProcessing(false);
      if (!isCleanedUp.current) {
        console.log('Disconnected from server');
      }
    };

    ws.onerror = () => {
      if (!isCleanedUp.current) {
        setError('WebSocket Error');
      }
    };

    wsRef.current = ws;

    return () => {
      isCleanedUp.current = true;
      evalTimers.forEach(clearTimeout);
      ws.close();
      stopAudioCapture();
    };
  }, [serverUrl, sessionContext, playAudioChunk, shouldConnect]);

  const startAudioCapture = useCallback(async () => {
    // Guard: don't start a second capture if one is already running
    if (streamRef.current) {
      console.log('[Audio] startAudioCapture called but stream already active — skipping');
      return;
    }
    try {
      console.log('[Audio] Requesting microphone...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Create a separate AudioContext for capture at 16kHz
      const captureContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 16000
      });
      captureContextRef.current = captureContext;

      const source = captureContext.createMediaStreamSource(stream);
      const processor = captureContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      source.connect(processor);
      processor.connect(captureContext.destination);

      audioSentRef.current = false;

      // Energy-based VAD — drives isUserSpeaking orb state locally
      // since Gemini native transcription emits finals only (no interims)
      let vadSilenceFrames = 0;
      const VAD_SILENCE_THRESHOLD = 8; // ~330ms at 4096 buffer / 16kHz
      const VAD_ENERGY_THRESHOLD = 0.01;

      processor.onaudioprocess = (e) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);

        // ── Local VAD ────────────────────────────────────────────────────────
        let rms = 0;
        for (let i = 0; i < inputData.length; i++) rms += inputData[i] * inputData[i];
        rms = Math.sqrt(rms / inputData.length);

        if (rms > VAD_ENERGY_THRESHOLD) {
          vadSilenceFrames = 0;
          setIsUserSpeaking(true);
        } else {
          vadSilenceFrames++;
          if (vadSilenceFrames >= VAD_SILENCE_THRESHOLD) {
            setIsUserSpeaking(false);
          }
        }

        // ── PCM16 encode & send to server ────────────────────────────────────
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          let s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        const buffer = new Uint8Array(pcm16.buffer);
        let binary = '';
        for (let i = 0; i < buffer.byteLength; i++) {
          binary += String.fromCharCode(buffer[i]);
        }
        const base64Data = btoa(binary);

        if (!audioSentRef.current) {
          console.log('[Audio] ✅ First audio chunk sent to server — pipeline is live');
          audioSentRef.current = true;
        }

        ws.send(JSON.stringify({
          realtimeInput: {
            audio: {
              mimeType: 'audio/pcm;rate=16000',
              data: base64Data
            }
          }
        }));
      };
    } catch (err: any) {
      setError(err.message || 'Failed to access microphone');
      console.error('Microphone error:', err);
    }
  }, []);

  const stopAudioCapture = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (captureContextRef.current) {
      captureContextRef.current.close().catch(() => {});
      captureContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch(e) {}
      recognitionRef.current = null;
    }
  }, []);

  const endSession = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'end_session' }));
      stopAudioCapture();
    }
  }, [stopAudioCapture]);

  const sendText = useCallback((text: string, speakReplies: boolean = false): boolean => {
    const ws = wsRef.current;
    const clean = text.trim();
    if (!clean) return false;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setError('AURA is reconnecting. Please try again in a moment.');
      return false;
    }
    // The server echoes the user bubble back via a `transcript` message, so we do NOT
    // render optimistically here — one source of truth for transcripts, no dedup.
    ws.send(JSON.stringify({ type: 'text_input', text: clean, speakReplies }));
    return true;
  }, []);

  return {
    isConnected,
    isGeminiReady,
    isSpeaking,
    isUserSpeaking,
    isProcessing,
    isMemorizing,
    memorySaved,
    savedMemory,
    isGeminiDropped,
    userJustFinished,
    userNeedsCorrection,
    error,
    transcripts,
    activeCard,
    mockResult,
    startAudioCapture,
    stopAudioCapture,
    endSession,
    sendText
  };
};
